import React, { useCallback, useRef, useState } from 'react';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, useSortable, horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Settings2, ArrowUp, ArrowDown, GripVertical } from 'lucide-react';

/**
 * <PreferenceTable>
 *
 * A shadcn <Table> wrapper that adds three per-user preferences on top of
 * the existing table primitives without changing how the underlying data
 * is fetched, sorted, filtered, or rendered:
 *
 *   • drag right edge of a header cell → column resize (px, min 60 max 1000)
 *   • drag a header cell horizontally  → column reorder (dnd-kit)
 *   • Columns popover                   → per-column visibility toggle + reset
 *
 * Rendering
 *   • Rows are memoised (React.memo + custom equality) so column-drag
 *     state updates on the parent don't re-render every row body.
 *   • Rows are virtualised with @tanstack/react-virtual over a real
 *     <table>/<tbody>. Uses the padding-row (sentinel <tr>) approach
 *     so no <tr> is ever positioned with translateY — avoids overlap /
 *     jump artefacts caused by CSS transforms on table rows.
 *   • Under `ROW_VIRT_THRESHOLD` rows we skip virtualisation entirely,
 *     because the DOM overhead of sentinel rows + scroll container is
 *     itself measurable for small tables.
 *
 * Sorting stays owned by the parent page (it passes `sortBy`, `sortDir`,
 * `onSort(colSortKey)` and each column config carries its own `sortKey`).
 * Every existing cell renderer runs unchanged inside `column.render(row)`.
 *
 * Props
 *   columns      full canonical column list [{id, label, defaultWidth,
 *                   sortKey?, alignRight?, render(row), cellClassName? }]
 *   rows         row objects to render
 *   rowKey       (row) => stable id (string). Defaults to row.id
 *   prefs        object from useTablePreferences (all fields)
 *   sortBy       colId currently sorted by (optional)
 *   sortDir      'asc' | 'desc'
 *   onSort       (col) => void — called with the column config when a
 *                   sortable header is clicked
 *   emptyMessage what to show when rows is empty
 *   rowClassName optional (row) => string
 *   'data-testid' optional prefix for stable selectors
 */

// Small tables: skip virtualiser + scroll container to keep the DOM
// identical to Phase 2 behaviour. 100 rows renders in ~50ms across all
// 5 pages so there is no user-facing cost to keeping it un-virtualised.
const ROW_VIRT_THRESHOLD = 100;
// Estimated row height (px) — the virtualiser measures the actual value
// after the first paint via ResizeObserver, so this is only a hint.
const ROW_ESTIMATE_PX = 52;
// Max height of the scroll container in virtualised mode. Everything
// past this is virtualised. 70vh matches the "long form" pattern used
// elsewhere in the app (dialogs, drawers).
const VIRT_MAX_HEIGHT = '70vh';

export default function PreferenceTable({
  columns,
  rows,
  rowKey = (r) => r.id,
  prefs,
  sortBy,
  sortDir = 'asc',
  onSort,
  emptyMessage = 'No rows to display.',
  rowClassName,
  onRowClick,
  'data-testid': testId,
}) {
  const { orderedVisibleColumns, widths, hiddenIds, setWidth, setOrder,
          toggleVisibility, resetPrefs } = prefs;

  // Column reorder (dnd-kit)
  const sensors = useSensors(useSensor(PointerSensor, {
    activationConstraint: { distance: 6 }, // require 6px drag so a click still fires
  }));
  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const current = orderedVisibleColumns.map(c => c.id);
    const oldIdx = current.indexOf(active.id);
    const newIdx = current.indexOf(over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    // Reorder within the FULL order array (respect hidden columns)
    const fullOrder = prefs.order.slice();
    const activeIdx = fullOrder.indexOf(active.id);
    const overIdx = fullOrder.indexOf(over.id);
    setOrder(arrayMove(fullOrder, activeIdx, overIdx));
  }, [orderedVisibleColumns, prefs.order, setOrder]);

  const totalCols = orderedVisibleColumns.length;
  const useVirtual = rows.length > ROW_VIRT_THRESHOLD;

  return (
    <div className="w-full" data-testid={testId}>
      {/* Column visibility popover */}
      <div className="flex justify-end mb-2">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" data-testid={`${testId}-columns-btn`}>
              <Settings2 className="w-4 h-4 mr-2" /> Columns
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-56">
            <div className="text-xs font-semibold text-slate-500 mb-2">SHOW / HIDE COLUMNS</div>
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {/* System columns (col.system === true, e.g. selection
                  checkbox) are pinned and cannot be hidden — they're
                  excluded from the toggle list entirely. */}
              {columns.filter(col => !col.system).map(col => (
                <label key={col.id} className="flex items-center gap-2 py-1 px-1 rounded hover:bg-slate-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!hiddenIds.has(col.id)}
                    onChange={() => toggleVisibility(col.id)}
                    data-testid={`${testId}-toggle-${col.id}`}
                  />
                  <span className="text-sm">{col.label}</span>
                </label>
              ))}
            </div>
            <div className="border-t border-slate-100 mt-2 pt-2">
              <Button variant="ghost" size="sm" onClick={resetPrefs} className="w-full text-xs" data-testid={`${testId}-reset`}>
                Reset to defaults
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        {useVirtual ? (
          <VirtualizedTableBody
            columns={orderedVisibleColumns}
            rows={rows}
            rowKey={rowKey}
            widths={widths}
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={onSort}
            setWidth={setWidth}
            emptyMessage={emptyMessage}
            rowClassName={rowClassName}
            onRowClick={onRowClick}
            testId={testId}
          />
        ) : (
          <PlainTableBody
            columns={orderedVisibleColumns}
            rows={rows}
            rowKey={rowKey}
            widths={widths}
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={onSort}
            setWidth={setWidth}
            emptyMessage={emptyMessage}
            rowClassName={rowClassName}
            onRowClick={onRowClick}
            testId={testId}
            totalCols={totalCols}
          />
        )}
      </DndContext>
    </div>
  );
}

/** Un-virtualised table body — used when rows.length <= threshold. Identical
 *  DOM to Phase 2 so small tables have zero regression risk. */
function PlainTableBody({
  columns, rows, rowKey, widths, sortBy, sortDir, onSort, setWidth,
  emptyMessage, rowClassName, onRowClick, testId, totalCols,
}) {
  return (
    <div className="border rounded-lg overflow-x-auto">
      <Table style={{ tableLayout: 'fixed', width: 'max-content', minWidth: '100%' }}>
        <colgroup>
          {columns.map(col => (
            <col key={col.id} style={{ width: `${widths[col.id] || col.defaultWidth || 160}px` }} />
          ))}
        </colgroup>
        <TableHeader>
          <SortableContext items={columns.map(c => c.id)} strategy={horizontalListSortingStrategy}>
            <TableRow>
              {columns.map(col => (
                <SortableHeader
                  key={col.id}
                  col={col}
                  width={widths[col.id] || col.defaultWidth || 160}
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={onSort}
                  onResize={setWidth}
                  testId={testId}
                />
              ))}
            </TableRow>
          </SortableContext>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={totalCols} className="text-center text-slate-500 py-8">{emptyMessage}</TableCell>
            </TableRow>
          ) : rows.map(row => (
            <MemoRow
              key={rowKey(row)}
              row={row}
              rowKeyValue={rowKey(row)}
              columns={columns}
              rowClassName={rowClassName}
              onRowClick={onRowClick}
              testId={testId}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/** Virtualised body — sentinel-row (padding row) technique so rows never
 *  overlap. Two zero-height <tr>s at the top and bottom of <tbody>
 *  absorb the un-rendered range's height; the middle range is actual
 *  <tr> elements. Because it's a real <table>, keyboard tab order and
 *  screen readers behave normally.
 *
 *  Renders <table> directly (NOT the shadcn <Table> wrapper) because
 *  shadcn wraps its <table> in `<div className="relative w-full overflow-auto">`.
 *  That inner div would become the sticky-header containing block and
 *  also create a second, unnecessary horizontal-scroll surface. Using
 *  raw <table> keeps our outer maxHeight:70vh div as the ONE scroll
 *  container, which is what position:sticky and react-virtual both
 *  need to measure against. */
function VirtualizedTableBody({
  columns, rows, rowKey, widths, sortBy, sortDir, onSort, setWidth,
  emptyMessage, rowClassName, onRowClick, testId,
}) {
  const scrollRef = useRef(null);
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_ESTIMATE_PX,
    overscan: 8,          // render a few extra rows above/below the viewport
    // Stable keys so React can reuse row DOM across scroll — critical for
    // the MemoRow shallow equality check to actually skip renders.
    getItemKey: (index) => rowKey(rows[index]),
    // Skip measureElement so scrollHeight stays stable while scrolling.
    // Rows in all 5 tables are single-line + uniform height, so the
    // ROW_ESTIMATE_PX hint is accurate enough and the scrollbar thumb
    // no longer drifts as previously-measured rows correct the estimate.
  });

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();
  const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0;
  const paddingBottom = virtualItems.length > 0
    ? totalSize - virtualItems[virtualItems.length - 1].end
    : 0;

  const totalCols = columns.length;

  return (
    <div
      ref={scrollRef}
      className="border rounded-lg overflow-auto relative"
      style={{ maxHeight: VIRT_MAX_HEIGHT }}
      data-testid={`${testId}-scroll`}
    >
      <table
        className="w-full caption-bottom text-sm"
        style={{ tableLayout: 'fixed', width: 'max-content', minWidth: '100%' }}
      >
        <colgroup>
          {columns.map(col => (
            <col key={col.id} style={{ width: `${widths[col.id] || col.defaultWidth || 160}px` }} />
          ))}
        </colgroup>
        <thead className="sticky top-0 z-10 bg-white [&_tr]:border-b shadow-sm">
          <SortableContext items={columns.map(c => c.id)} strategy={horizontalListSortingStrategy}>
            <TableRow>
              {columns.map(col => (
                <SortableHeader
                  key={col.id}
                  col={col}
                  width={widths[col.id] || col.defaultWidth || 160}
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={onSort}
                  onResize={setWidth}
                  testId={testId}
                />
              ))}
            </TableRow>
          </SortableContext>
        </thead>
        <tbody className="[&_tr:last-child]:border-0">
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={totalCols} className="text-center text-slate-500 py-8">{emptyMessage}</TableCell>
            </TableRow>
          ) : (
            <>
              {paddingTop > 0 && (
                <tr aria-hidden="true" style={{ height: `${paddingTop}px` }} data-testid={`${testId}-vpad-top`}>
                  <td colSpan={totalCols} style={{ padding: 0, border: 0 }} />
                </tr>
              )}
              {virtualItems.map(item => {
                const row = rows[item.index];
                return (
                  <MemoRow
                    key={rowKey(row)}
                    row={row}
                    rowKeyValue={rowKey(row)}
                    columns={columns}
                    rowClassName={rowClassName}
                    onRowClick={onRowClick}
                    testId={testId}
                    dataIndex={item.index}
                  />
                );
              })}
              {paddingBottom > 0 && (
                <tr aria-hidden="true" style={{ height: `${paddingBottom}px` }} data-testid={`${testId}-vpad-bottom`}>
                  <td colSpan={totalCols} style={{ padding: 0, border: 0 }} />
                </tr>
              )}
            </>
          )}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Single row, memoised. Skips re-render if the same row object,
 * the same columns array (reference), and the same click/classname
 * callbacks were passed. Because parent passes `columns=orderedVisibleColumns`
 * (a new array on every render) we compare BY VALUE for `columns` — id
 * sequence + width — since only those two affect the visible output.
 */
const MemoRow = React.memo(function MemoRow({
  row, rowKeyValue, columns, rowClassName, onRowClick, testId, dataIndex,
}) {
  return (
    <TableRow
      data-index={dataIndex}
      className={rowClassName ? rowClassName(row) : undefined}
      onClick={onRowClick ? () => onRowClick(row) : undefined}
      data-testid={`${testId}-row-${rowKeyValue}`}
    >
      {columns.map(col => (
        <TableCell
          key={col.id}
          className={`${col.alignRight ? 'text-right ' : ''}${col.cellClassName || ''} overflow-hidden text-ellipsis`}
          style={{ maxWidth: 0 }}   // required so overflow:hidden respects the col width
        >
          {col.render(row)}
        </TableCell>
      ))}
    </TableRow>
  );
}, (prev, next) => {
  // Skip re-render when nothing that affects THIS row changed.
  if (prev.row !== next.row) return false;
  if (prev.rowKeyValue !== next.rowKeyValue) return false;
  if (prev.onRowClick !== next.onRowClick) return false;
  if (prev.rowClassName !== next.rowClassName) return false;
  if (prev.testId !== next.testId) return false;
  if (prev.dataIndex !== next.dataIndex) return false;
  // Columns array is recreated by the parent every render but its
  // meaningful contents are stable across most re-renders. Compare by
  // id + cellClassName + alignRight signature; skip render function
  // identity (parent inlines them, so they'd always mismatch).
  const a = prev.columns, b = next.columns;
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i].id !== b[i].id) return false;
    if (a[i].alignRight !== b[i].alignRight) return false;
    if (a[i].cellClassName !== b[i].cellClassName) return false;
    // We intentionally DO NOT check render fn identity — parents inline
    // render lambdas so identity flips on every parent render. This is
    // safe because a parent that changes the underlying row data is
    // required to give us a new `row` reference too, which we already
    // catch above.
    //
    // EXCEPTION (2026-09-01): `system` columns (e.g. proposals' `_select`
    // checkbox) render based on state OUTSIDE the row itself (a
    // selectedIds Set living in the parent) — the "same row = same
    // output" assumption above does not hold for them. Without this,
    // clicking a checkbox updates state correctly but the row never
    // re-renders, so the checkbox visually never shows as checked.
    if (a[i].system || b[i].system) return false;
  }
  return true;
});

/** Header cell — sortable via drag AND clickable-for-sort AND has a resize grip. */
function SortableHeader({ col, width, sortBy, sortDir, onSort, onResize, testId }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: col.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative',
  };
  const resizingRef = useRef(false);
  const startXRef = useRef(0);
  const startWRef = useRef(width);
  const [preview, setPreview] = useState(null);

  const onGripDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    resizingRef.current = true;
    startXRef.current = e.clientX;
    startWRef.current = width;
    setPreview(width);
    const onMove = (ev) => {
      if (!resizingRef.current) return;
      const delta = ev.clientX - startXRef.current;
      setPreview(Math.max(60, Math.min(1000, startWRef.current + delta)));
    };
    const onUp = (ev) => {
      if (!resizingRef.current) return;
      resizingRef.current = false;
      const delta = ev.clientX - startXRef.current;
      onResize(col.id, Math.max(60, Math.min(1000, startWRef.current + delta)));
      setPreview(null);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const isSorted = col.sortKey && sortBy === col.sortKey;
  const isSortable = !!col.sortKey && !!onSort;

  return (
    <TableHead
      ref={setNodeRef}
      style={{ ...style, width: `${preview || width}px` }}
      className={`select-none group ${col.alignRight ? 'text-right' : ''}`}
      data-testid={`${testId}-head-${col.id}`}
    >
      <div className="flex items-center gap-1 pr-2">
        <span
          className="cursor-grab active:cursor-grabbing opacity-30 group-hover:opacity-100 transition-opacity"
          {...attributes}
          {...listeners}
          data-testid={`${testId}-drag-${col.id}`}
        >
          <GripVertical className="w-3 h-3" />
        </span>
        <span
          className={`flex-1 truncate ${isSortable ? 'cursor-pointer hover:text-slate-900' : ''}`}
          onClick={isSortable ? () => onSort(col) : undefined}
        >
          {col.label}
          {isSorted && (sortDir === 'asc'
            ? <ArrowUp className="inline w-3 h-3 ml-1" />
            : <ArrowDown className="inline w-3 h-3 ml-1" />)}
        </span>
      </div>
      {/* Resize grip on the right edge */}
      <span
        onMouseDown={onGripDown}
        className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-blue-400 active:bg-blue-600"
        data-testid={`${testId}-resize-${col.id}`}
      />
    </TableHead>
  );
}
